import "whatwg-fetch";
import _ from "lodash";
import queryString from "query-string";

import React from "react";
import {Modal} from "antd";

import FileSaver from "file-saver";
import decodeUriComponent from "decode-uri-component";

import urljoin from "url-join";
import ServiceException from "../exception/ServiceException";
import Constants from "../constant/Constants";

import AppContext from "../application/AppContext";
import {update} from "../viewmodel/mainViewModel";

export const FILE_SAVE_AS_ORIGINAL_NAME = "FILE_SAVE_AS_ORIGINAL_NAME"; // 由于之前的代码导出文件时前端都进行了重命名；自定义参数判断保存文件时是否使用原来的名称 或是传回文件重命名
const _baseUrl = "../../";


function processUrl(url) {
    return _.replace(url, "{BASE_URL}", _baseUrl);
}
const getHeaders = (method) => {
    const headers = {};
    headers["X-LF-AGENT"] = "PC";
    if (AppContext.user && AppContext.user.hospital && method === "POST" && !AppContext.isSwjw) {
        headers["X-NC-HOSPITALID"] = AppContext.user.hospital.id;
    }
    const queryArg = queryString.parse(location.search);
    if (queryArg.corpid) {
        headers["LF-CORPID"] = queryArg.corpid;
    }

    if (AppContext.ssoAccessToken != null) {
        headers["X-NC-ACCESSTOKEN"] = AppContext.ssoAccessToken;
    }
    const token = localStorage.getItem(Constants.TOKEN_NAME);
    if (token) {
        headers["X-NC-ACCESSTOKEN"] = token;
    }

    return headers;
};


const requestCore = async (option) => {
    let {
        path = "",
        param = {},
        method = "POST",
        contentType = "json",
        responseType = "json",
        baseUrl = Constants.BASE_URL,
    } = option;

    const headers = new Headers(getHeaders(method));

    if (contentType === "formData") {
        headers.append("Accept", "*/*");
    } else {
        headers.append("Accept", "application/json");
    }

    const fileSaveAsOriginalName = param[FILE_SAVE_AS_ORIGINAL_NAME] ?? false;
    param = _.omit(param, FILE_SAVE_AS_ORIGINAL_NAME);

    let body;
    if (contentType === "json") {
        headers.append("Content-Type", "application/json");
        body = JSON.stringify(param);
    } else if (contentType === "formData") {
        body = new FormData();
        _.forEach(param, (it, key) => {
            if (it instanceof Blob && !_.isEmpty(it.name)) {
                body.append(key, it, it.name);
            } else {
                body.append(key, it);
            }
        });
    } else {
        // query params
        if (param != null) {
            path = `${path}?${queryString.stringify(param)}`;
        }
    }
    // https://stackoverflow.com/questions/30967822/when-do-i-use-path-params-vs-query-params-in-a-restful-api
    // TODO : path params

    let response;
    try {
        response = await fetch(urljoin(baseUrl, path), {
            credentials: "same-origin",
            method,
            body,
            headers,
        });
    } catch (e) {
        console.log("fetch", e);
        throw new ServiceException(
            ServiceException.UNKNOWN,
            "网络连接失败，请重试!",
            null,
            path);
    }

    if (responseType === "json") {
        if (response && response.text && response.status) {
            const text = await response.text();

            let result = null;
            if (text !== "") {
                try {
                    result = JSON.parse(text);
                } catch (e) {
                    console.warn(e);
                    console.log(text);
                }
            }

            if (response.status >= 200
                && response.status < 300) {
                return result;
            } else if (response.status === 400
                || response.status === 401
                || response.status === 500) {
                // griffin 自定义错误

                if (result !== null) {
                    throw new ServiceException(
                        result.code,
                        result.errMsg,
                        result.level,
                        path,
                        ServiceException.CUSTOM_TYPE);
                } else {
                    throw new ServiceException(
                        ServiceException.UNKNOWN,
                        "网络连接失败，请重试!",
                        null,
                        path);
                }
            } else if (response.status > 400
                && response.status < 500) {
                // 服务器 spring 返回格式 :
                // message
                // path
                // level
                // ...
                //
                // spring 返回错误
                // 400 表示参数提交不合法
                if (result !== null) {
                    throw new ServiceException(
                        ServiceException.CLIENT_ERROR,
                        result.message,
                        null,
                        path);
                } else {
                    throw new ServiceException(
                        ServiceException.CLIENT_ERROR,
                        "网络错误，请稍后重试!",
                        null,
                        path);
                }
            } else if (response.status > 500
                && response.status < 600) {
                // spring 返回错误
                // 500 表示服务计算错误

                if (result !== null) {
                    throw new ServiceException(
                        ServiceException.SERVER_ERROR,
                        result.message,
                        null,
                        path);
                } else {
                    throw new ServiceException(
                        ServiceException.SERVER_ERROR,
                        "网络错误，请稍后重试!",
                        null,
                        path);
                }
            } else {
                throw new ServiceException(
                    ServiceException.UNKNOWN,
                    "网络连接失败，请重试!",
                    null,
                    path);
            }
        } else {
            throw new ServiceException(
                ServiceException.UNKNOWN,
                "网络连接失败，请重试!",
                null,
                path);
        }
    } else if (responseType === "file") {
        let filename = "";
        const disposition = response.headers.get("Content-Disposition");
        if (disposition && disposition.indexOf("attachment") !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, "");
            }
        }

        const status = response.status;
        if (status >= 200
            && status < 300) {
            const b = await response.blob();
            // https://github.com/sindresorhus/query-string
            // https://github.com/kevva/strict-uri-encode
            // https://github.com/SamVerschueren/decode-uri-component
            if (fileSaveAsOriginalName) {
                FileSaver.saveAs(b, decodeUriComponent(filename));
            }
            return b;
        } else {
            throw new ServiceException(
                ServiceException.UNKNOWN,
                "文件下载失败!",
                null,
                path,
            );
        }
    }
};

const _request = option => new Promise((resolve, reject) => requestCore(option)
    .then(resolve, reject));

const request = option => new Promise((resolve, reject) => requestCore(option)
    .then(
        resolve,
        (error) => {
            if (error.code === Constants.NO_AUTH_CODE) {
                // window.location.href = "login.html";
                AppContext.store.dispatch(update({
                    error,
                }));
            } else if (error.code === Constants.EXPIRED_CODE) {
                AppContext.store.dispatch(update({
                    isExpired: true,
                }));
            } else if (error.code === Constants.HOSPITAL_UNDERLAY_TRANSFERED_CODE
                || error.message === "未知错误:120005"
            ) {
                AppContext.store.dispatch(update({
                    isUnderlayTransfered: true,
                }));
            } else {
                Modal.error({
                    iconType: "null",
                    title: error.message,
                });
            }

            reject(error);
            // if (error.type !== ServiceException.CUSTOM_TYPE) {
            //     // 自定义错误不被抛出，防止监控平台监控此错误
            //     reject(error);
            // }
        }));


export default {
    _request,
    request,
    getHeaders,
};
